import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

// PrimeNG Components
import { MenubarModule } from 'primeng/menubar';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { AuthService, User } from '../../../core/services/auth.service';
import { NGXLogger } from 'ngx-logger';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { MaterialIconComponent } from '../../components/material-icon/material-icon.component';
import { PecNotificationBellComponent } from '../pec-notification-bell/pec-notification-bell.component';
import { environment } from '../../../../environments/environment';
import { ChangelogDialogComponent } from '../changelog-dialog/changelog-dialog.component';
import { SupportTicketDialogComponent } from '../support-ticket-dialog/support-ticket-dialog.component';
import { WipDialogComponent } from '../wip-dialog/wip-dialog.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterModule,
    MenubarModule,
    ButtonModule,
    AvatarModule,
    MenuModule,
    BadgeModule,
    TooltipModule,
    TranslatePipe,
    MaterialIconComponent,
    PecNotificationBellComponent,
    WipDialogComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  providers: [DialogService],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('userMenu') userMenu!: Menu;

  isAuthenticated = false;
  currentUser: User | null = null;
  showMobileMenu = false;
  showConfigMenu = false;
  showSupportMenu = false;
  showFascicoliMenu = false;
  showControlliMenu = false;
  showManualeWipDialog = false;
  userMenuItems: MenuItem[] = [];
  currentRoute = '';
  appVersion = environment.version || '0.0.0';
  environmentName = environment.name;
  isIstruttoreOrControlloreUser = false;

  private subscriptions = new Subscription();
  private changelogDialogRef?: DynamicDialogRef;
  private supportTicketDialogRef?: DynamicDialogRef;

  constructor(
    private authService: AuthService,
    private logger: NGXLogger,
    private router: Router,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.initializeUserMenu();
    this.subscribeToAuth();
    this.subscribeToRouter();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  goToHome() {
    this.router.navigate(['/']);
  }

  scrollToSection(sectionId: string) {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private initializeUserMenu() {
    this.userMenuItems = [
      {
        label: 'Profilo',
        icon: 'pi pi-user',
        routerLink: '/profile',
      },
      {
        label: 'Impostazioni',
        icon: 'pi pi-cog',
        routerLink: '/settings',
      },
      {
        separator: true,
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => this.logout(),
      },
    ];
  }

  onUserMenuToggle(event: Event) {
    event.stopPropagation();
    this.userMenu.toggle(event);
  }

  private subscribeToAuth() {
    this.subscriptions.add(
      this.authService.isAuthenticated$.subscribe((isAuth) => {
        this.isAuthenticated = isAuth;
        // Aggiorna lo stato del ruolo quando cambia l'autenticazione
        this.updateIstruttoreOrControlloreStatus();
        //console.log('[Navbar] Stato autenticazione aggiornato:', isAuth);
        //  this.logger.info('[NAVBAR] Stato autenticazione aggiornato:', isAuth);
      }),
    );

    this.subscriptions.add(
      this.authService.user$.subscribe((user) => {
        this.currentUser = user;
        // Aggiorna lo stato del ruolo quando cambia l'utente
        this.updateIstruttoreOrControlloreStatus();
        // console.log('[Navbar] Utente aggiornato:', user);
        //  this.logger.info('[NAVBAR] Utente aggiornato:', user);
      }),
    );
  }

  private updateIstruttoreOrControlloreStatus() {
    const ruolo = localStorage.getItem('ruolo');
    this.isIstruttoreOrControlloreUser =
      ruolo === 'ISTRUTTORE' || ruolo === 'CONTROLLORE';
    // Log temporaneo per debug
    if (this.isIstruttoreOrControlloreUser) {
      this.logger.info(
        '[NAVBAR] Utente è ISTRUTTORE/CONTROLLORE, ruolo:',
        ruolo,
      );
    }
  }

  private subscribeToRouter() {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.currentRoute = event.url;
        }),
    );
  }

  login() {
    this.logger.info("[NAVBAR] Login richiesto dall'utente");
    this.authService.login();
  }

  logout() {
    this.logger.info("[NAVBAR] Logout richiesto dall'utente");
    this.authService.logout();
  }

  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  toggleConfigMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showConfigMenu = !this.showConfigMenu;
    this.showFascicoliMenu = false;
    this.showControlliMenu = false;
    this.showSupportMenu = false;
  }

  closeConfigMenu() {
    this.showConfigMenu = false;
  }

  toggleSupportMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showSupportMenu = !this.showSupportMenu;
    this.showFascicoliMenu = false;
    this.showControlliMenu = false;
    this.showConfigMenu = false;
  }

  closeSupportMenu() {
    this.showSupportMenu = false;
  }
  closeControlliMenu() {
    this.showControlliMenu = false;
  }

  toggleFascicoliMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showFascicoliMenu = !this.showFascicoliMenu;
    this.showControlliMenu = false;
    this.showConfigMenu = false;
    this.showSupportMenu = false;
  }
  toggleControlliMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showControlliMenu = !this.showControlliMenu;
    this.showFascicoliMenu = false;
    this.showConfigMenu = false;
    this.showSupportMenu = false;
  }

  closeFascicoliMenu() {
    this.showFascicoliMenu = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.nav-dropdown')) {
      this.showControlliMenu = false;
      this.showConfigMenu = false;
      this.showSupportMenu = false;
      this.showFascicoliMenu = false;
    }
  }

  getInitials(name: string): string {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  getRoleChipClass(): string {
    const ruolo = localStorage.getItem('ruolo');
    if (!ruolo) return 'role-default';

    return `role-${ruolo.toLowerCase()}`;
  }

  getRoleDisplayName(): string {
    const ruolo = localStorage.getItem('ruolo');
    if (!ruolo) return 'Utente';

    const roleMap: { [key: string]: string } = {
      SUPER_ADMIN: 'Super Admin',
      ADMIN: 'Admin',
      BENEFICIARIO: 'Beneficiario',
      RESPONSABILE_FASCICOLO: 'Resp. Fascicolo',
      ISTRUTTORE: 'Istruttore',
      ISTRUTTORE_DOMANDA_SOSTEGNO: 'Istr. Sostegno',
      ISTRUTTORE_DOMANDA_PAGAMENTO: 'Istr. Pagamento',
      CONTROLLORE: 'Controllore',
      ISPETTORE: 'Ispettore',
      PA: 'P.A.',
    };

    return roleMap[ruolo] || ruolo;
  }

  isConfigMenuActive(): boolean {
    const configRoutes = ['/utenti', '/ruoli', '/permessi', '/impostazioni'];
    return configRoutes.some((route) => this.currentRoute.startsWith(route));
  }

  isSupportMenuActive(): boolean {
    const supportRoutes = ['/supporto/faq', '/supporto/segnalazioni'];
    return supportRoutes.some((route) => this.currentRoute.startsWith(route));
  }

  isFascicoliMenuActive(): boolean {
    return (
      this.currentRoute.startsWith('/fascicoli') ||
      this.currentRoute.startsWith('/beneficiari')
    );
  }
  isControlliMenuActive(): boolean {
    const configRoutes = ['/controlli-in-situ', '/controllori', '/istruttori'];
    return configRoutes.some((route) => this.currentRoute.startsWith(route));
  }

  isBeneficiario(): boolean {
    const ruolo = localStorage.getItem('ruolo');
    return ruolo === 'BENEFICIARIO';
  }

  isIstruttoreOrControllore(): boolean {
    const ruolo = localStorage.getItem('ruolo');
    return ruolo === 'ISTRUTTORE' || ruolo === 'CONTROLLORE';
  }

  openChangelog() {
    this.changelogDialogRef = this.dialogService.open(
      ChangelogDialogComponent,
      {
        header: '📋 Changelog AgriFlow',
        width: '70vw',
        height: '75vh',
        maximizable: false,
        modal: true,
        dismissableMask: true,
        closable: true,
        baseZIndex: 2000000,
        styleClass: 'changelog-dialog-container',
        contentStyle: {
          padding: '0',
          overflow: 'hidden',
        },
      },
    );
  }

  openSupportTicket() {
    this.logger.info('[NAVBAR] Apertura dialog richiesta supporto');

    this.supportTicketDialogRef = this.dialogService.open(
      SupportTicketDialogComponent,
      {
        header: '🎫 Richiesta di Supporto',
        width: '650px',
        height: '75vh',
        modal: true,
        dismissableMask: true,
        closable: true,
        baseZIndex: 2000000,
        styleClass: 'support-ticket-dialog-container',
        contentStyle: {
          padding: '0',
          overflow: 'hidden',
          height: '100%',
        },
        data: {
          userName: this.currentUser?.name || '',
          userEmail: this.currentUser?.email || '',
        },
      },
    );

    this.supportTicketDialogRef.onClose.subscribe((result: any) => {
      if (result?.success) {
        this.logger.info('[NAVBAR] Ticket di supporto creato con successo');
      } else {
        this.logger.info('[NAVBAR] Dialog di supporto chiuso senza inviare');
      }
    });
  }

  openManualeWip(): void {
    this.logger.info('[NAVBAR] Apertura dialog manuale in elaborazione');
    this.showManualeWipDialog = true;
  }

  onManualeWipClose(): void {
    this.showManualeWipDialog = false;
  }
}
